
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { Clipboard, Calendar } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { format } from 'date-fns';
import { BASE_URL } from '@/config/api';
import { eventService } from '@/services/eventService';

interface PasteConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventIds: string[];
  targetView: string;
  targetDate: Date;
  targetRange?: { start: Date; end: Date };
  sourceDate?: Date;
  sourceRange?: { start: Date; end: Date };
  token: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const PasteConfirmationModal = ({
  open,
  onOpenChange,
  eventIds,
  targetView,
  targetDate,
  targetRange,
  sourceDate,
  sourceRange,
  token,
  onSuccess,
  onError
}: PasteConfirmationModalProps) => {
  const { t, locale } = useLanguage();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);

  const formatDateString = (date: Date) => {
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(date);
  };

  const formatDateRange = () => {
    if (targetView === 'timeGridDay') {
      return formatDateString(targetDate);
    } else if (targetRange) {
      return `${formatDateString(targetRange.start)} - ${formatDateString(targetRange.end)}`;
    }
    return '';
  };

  const formatSourceDateRange = () => {
    if (sourceDate) {
      return formatDateString(sourceDate);
    } else if (sourceRange) {
      return `${formatDateString(sourceRange.start)} - ${formatDateString(sourceRange.end)}`;
    }
    return '';
  };

  const handlePaste = async () => {
    if (eventIds.length === 0) {
      onError(t('agenda.noEventsSelected'));
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${BASE_URL}/api/paste_events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          eventIds,
          targetDate: format(targetDate, 'yyyy-MM-dd'),
          targetView,
          targetRange: targetRange ? {
            start: format(targetRange.start, 'yyyy-MM-dd'),
            end: format(targetRange.end, 'yyyy-MM-dd')
          } : undefined,
          sourceDate: sourceDate ? format(sourceDate, 'yyyy-MM-dd') : undefined,
          sourceRange: sourceRange ? {
            start: format(sourceRange.start, 'yyyy-MM-dd'),
            end: format(sourceRange.end, 'yyyy-MM-dd')
          } : undefined
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('agenda.pasteError'));
      }
      
      onOpenChange(false);
      eventService.clearEventsCache();
      onSuccess();
      
    } catch (error) {
      console.error('Error pasting events:', error);
      onError(error instanceof Error ? error.message : t('agenda.pasteError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-md"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-2"}>
          <DialogTitle className="flex items-center gap-2">
            <Clipboard className="h-5 w-5 text-primary" />
            {t('agenda.pasteEvents')}
          </DialogTitle>
          <DialogDescription>
            {t('agenda.pasteEventsConfirmation')}
          </DialogDescription>
        </DialogHeader>

        <div className={`py-4 ${isMobile ? "px-6" : ""}`}>
          <div className="space-y-4">
            <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{t('agenda.sourceTimeframe')}:</p>
                <p className="text-sm">{formatSourceDateRange()}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 p-4 bg-primary/10 rounded-lg">
              <Calendar className="h-5 w-5 text-primary" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{t('agenda.targetTimeframe')}:</p>
                <p className="text-sm">{formatDateRange()}</p>
              </div>
            </div>
            
            <p className="text-center font-medium">
              {t('agenda.willPaste')} <span className="text-primary">{eventIds.length}</span> {eventIds.length > 1 ? t('agenda.events') : t('agenda.event')}
            </p>
          </div>
        </div>

        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-background z-20" : "pt-5"}>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handlePaste}
            disabled={loading}
            className="bg-primary hover:bg-primary/90"
          >
            {loading ? (
              <span className="flex items-center">
                <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                {t('common.processing')}
              </span>
            ) : (
              t('agenda.confirmPaste')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PasteConfirmationModal;
